import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Sidebar.scss";

import { Link } from "react-router-dom";
import { useHttpClient } from "../../hooks/http-hook";
import logo from "./logo.png";

import { useContext } from "react";
import { AuthContext } from "../../context/auth-context";
import { ClinicaContext } from "../../context/clinica-context";
import { Notebook } from "lucide-react";

import {
  LayoutDashboard, // Para DashboardIcon
  UserCircle, // Para PersonOutlineOutlinedIcon
  Package2, // Para Inventory2OutlinedIcon
  PillIcon, // Para MedicationOutlinedIcon (usando PillIcon em vez de Pill)
  CircleUser, // Para AccountCircleOutlinedIcon
  LogOut, // Para LogoutOutlinedIcon
  BanknoteIcon, // Para AttachMoneyIcon
  Users, // Para GroupIcon
  PackageCheck, // Para Inventory2Icon
  BarChart3, // Para BarChartIcon
  Settings, // Para SettingsIcon
  ClipboardList, // Para ListAltIcon
  UserRoundCog, // Para Funcionarios,
  CalendarClock, // Para Lista de espera
  Wrench, // Para Manutenção
  BookOpenText, //Para Documentação
} from "lucide-react";

function Sidebar() {
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const navigate = useNavigate();
  const [medico, setMedico] = useState();
  const location = useLocation();
  const [activeItem, setActiveItem] = useState("/");

  /* Pedidos vindos da página pública à espera de decisão. Sem este número, a
     entrada do menu é mais uma a ignorar — e do outro lado está alguém à
     espera de um telefonema que não vem. */
  const [pedidosPendentes, setPedidosPendentes] = useState(0);
  /* Quantos desses são urgentes. Só serve para a cor: o número que se mostra
     continua a ser o total. */
  const [pedidosUrgentes, setPedidosUrgentes] = useState(0);

  const { sendRequest } = useHttpClient();

  const logout = () => {
    navigate("/");
    auth.logout();
  };

  // Efeito para definir o item ativo com base na URL atual
  useEffect(() => {
    // Extraímos o caminho principal da URL
    const path = location.pathname;

    // Verificar condições especiais para certas rotas
    if (path.includes("/perfil")) {
      setActiveItem("/perfil");
    }
    // Caso especial para agendamentosviewonly
    else if (path.includes("/agendamentosviewonly")) {
      setActiveItem("/agendamentos");
    } else if (path.includes("/agendamentostratamento")) {
      setActiveItem("/agendamentos");
    }
    // Para rotas padrão, usamos o primeiro segmento
    else {
      const firstSegment = "/" + (path.split("/")[1] || "");
      setActiveItem(firstSegment);
    }
  }, [location]);

  /* O contador acompanha a navegação: decidir um pedido leva de volta à lista,
     e o número tem de descer sem obrigar a recarregar a página. */
  useEffect(() => {
    if (!auth.perm?.includes("c-marc")) return;

    let montado = true;

    const contar = async () => {
      try {
        const r = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/pedidosmarcacao/pendentes/${clinica.clinica._id}/contador`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        if (montado) {
          setPedidosPendentes(r.total || 0);
          setPedidosUrgentes(r.urgentes || 0);
        }
      } catch {
        /* Um contador que não carrega não deve partir o menu. Fica a zero e a
           entrada continua lá. */
      }
    };

    contar();
    return () => {
      montado = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token, auth.perm, clinica.clinica._id, location.pathname]);

  useEffect(() => {
    if (auth.role === "Gerente") {
      const fetchMedicos = async () => {
        try {
          const response = await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/users/ativos/${clinica.clinica._id}`,
            "GET",
            null,
            {
              Authorization: "Bearer " + auth.token,
            },
          );

          const temp = response.users.filter(
            (u) =>
              u.role.role === "Dentista" ||
              u.role.role === "Médico/Administrador",
          );
          setMedico({ id: temp[0].id, nome: temp[0].name });
        } catch (err) {}
      };
      fetchMedicos();
    }
  }, [auth.role, auth.token, clinica.clinica._id, sendRequest]);

  // Função para determinar se um item está ativo
  const isActive = (path) => {
    return activeItem === path;
  };

  // Função para gerar a classe CSS do item de menu
  const getItemClass = (path) => {
    return `sidebar__li ${isActive(path) ? "sidebar__li--active" : ""}`;
  };

  return (
    <>
      {auth && auth.perm && (
        <div className="sidebar">
          <div className="sidebar__top">
            <Link to="/" style={{ textDecoration: "none" }}>
              <img className="sidebar__logo" src={logo} alt="" />
            </Link>
          </div>

          <ul className="sidebar__ul">
            <div className="sidebar__iconExpand__container">
              <p className="sidebar__title">MENU</p>
            </div>

            <Link to="/" style={{ textDecoration: "none" }}>
              <li className={getItemClass("/") + " sidebar__li"}>
                <LayoutDashboard className="sidebar__icon" />
                <span className="sidebar__span">Dashboard</span>
              </li>
            </Link>

            {auth.perm.includes("r-paAg") &&
              (auth.role === "Dentista" ||
                auth.role === "Médico/Administrador") && (
                <Link
                  to={`/${
                    clinica.clinica.configuracoes.configuracoes.tipoAgendamento
                      .tipo === "Tratamento"
                      ? auth.perm.includes("c-marc")
                        ? "agendamentostratamento"
                        : "agendamentostratamentoviewonly"
                      : auth.perm.includes("c-marc")
                        ? "agendamentos"
                        : "agendamentosviewonly"
                  }/${auth.userId}`}
                  style={{ textDecoration: "none" }}
                >
                  <li
                    className={getItemClass("/agendamentos") + " sidebar__li"}
                  >
                    <Package2 className="sidebar__icon" />
                    <span className="sidebar__span">Agendamentos</span>
                  </li>
                </Link>
              )}

            {auth.perm.includes("r-paAg") &&
              auth.role === "Secretária(o)" &&
              auth.usuariosAssociados && (
                <Link
                  to={`/${
                    clinica.clinica.configuracoes.configuracoes.tipoAgendamento
                      .tipo === "Tratamento"
                      ? "agendamentostratamento"
                      : "agendamentos"
                  }/${auth.usuariosAssociados[0].id}`}
                  style={{ textDecoration: "none" }}
                >
                  <li
                    className={getItemClass("/agendamentos") + " sidebar__li"}
                  >
                    <Package2 className="sidebar__icon" />

                    <span className="sidebar__span">Agendamentos</span>
                  </li>
                </Link>
              )}

            {auth.perm.includes("r-paAg") &&
              auth.role === "Assistente" &&
              auth.usuariosAssociados && (
                <Link
                  to={`/${
                    clinica.clinica.configuracoes.configuracoes.tipoAgendamento
                      .tipo === "Tratamento"
                      ? "agendamentostratamentoviewonly"
                      : "agendamentosviewonly"
                  }/${auth.usuariosAssociados[0].id}`}
                  style={{ textDecoration: "none" }}
                >
                  <li
                    className={getItemClass("/agendamentos") + " sidebar__li"}
                  >
                    <Package2 className="sidebar__icon" />

                    <span className="sidebar__span">Agendamentos</span>
                  </li>
                </Link>
              )}

            {/* Rota para ver agendamentos de Gerente */}
            {auth.perm.includes("r-paAg") &&
              auth.role === "Gerente" &&
              //O codigo seguinte apresenta a opcao agendamentos quando os medicos forem selecionados
              //Caso ainda não houverem medicos vindos da base de dados, é apresentado o texto Agendamentos
              //Porem sem funcao alguma. Apenas para evita uma glitch no ecra
              (medico ? (
                <Link
                  to={
                    clinica.clinica.configuracoes.configuracoes.tipoAgendamento
                      .tipo === "Tratamento"
                      ? `/agendamentostratamentoviewonly/${medico.id}`
                      : `/agendamentosviewonly/${medico.id}`
                  }
                  style={{ textDecoration: "none" }}
                >
                  <li
                    className={getItemClass("/agendamentos") + " sidebar__li"}
                  >
                    <Package2 className="sidebar__icon" />

                    <span className="sidebar__span">Agendamentos</span>
                  </li>
                </Link>
              ) : (
                <li className={getItemClass("/agendamentos") + " sidebar__li"}>
                  <Package2 className="sidebar__icon" />

                  <span className="sidebar__span">Agendamentos</span>
                </li>
              ))}

            {/* Pedidos da página pública. A entrada só aparece quando há
                pedidos à espera: um menu que mostra sempre "0" ensina-se a
                ignorar, e esta clínica pode nem ter a página ligada. */}
            {auth.perm.includes("c-marc") && pedidosPendentes > 0 && (
              <Link to="/pedidosmarcacao" style={{ textDecoration: "none" }}>
                <li
                  className={getItemClass("/pedidosmarcacao") + " sidebar__li"}
                >
                  <CalendarClock className="sidebar__icon" />
                  <span className="sidebar__span">Pedidos de marcação</span>
                  {/* Vermelho quando há pelo menos um urgente. É a única
                      coisa que a secretária vê sem abrir nada — e por isso é
                      a cor que muda, não o número: trocar o número por «1
                      urgente» escondia quantos pedidos há ao todo. */}
                  <span
                    className={
                      "sidebar__contador" +
                      (pedidosUrgentes > 0 ? " sidebar__contador--urgente" : "")
                    }
                    title={
                      pedidosUrgentes > 0
                        ? `${pedidosUrgentes} pedido(s) de urgência à espera de contacto`
                        : undefined
                    }
                  >
                    {pedidosPendentes}
                  </span>
                </li>
              </Link>
            )}

            {auth.perm.includes("r-paCli") && (
              <Link to="/clientes" style={{ textDecoration: "none" }}>
                <li className={getItemClass("/clientes") + " sidebar__li"}>
                  <UserCircle className="sidebar__icon" />
                  <span className="sidebar__span">Clientes</span>
                </li>
              </Link>
            )}

            {auth.perm.includes("r-paTra") && (
              <Link to="/tratamentos" style={{ textDecoration: "none" }}>
                <li className={getItemClass("/tratamentos") + " sidebar__li"}>
                  <PillIcon className="sidebar__icon" />

                  <span className="sidebar__span">Tratamentos</span>
                </li>
              </Link>
            )}

            {auth.role === "Secretária(o)" && (
              <Link to="/listaespera" style={{ textDecoration: "none" }}>
                <li className={getItemClass("/tratamentos") + " sidebar__li"}>
                  <CalendarClock className="sidebar__icon" />

                  <span className="sidebar__span">Lista de Espera</span>
                </li>
              </Link>
            )}

            {auth.perm.includes("r-paFi") && (
              <Link to="/financeiro" style={{ textDecoration: "none" }}>
                <li className={getItemClass("/financeiro") + " sidebar__li"}>
                  <BanknoteIcon className="sidebar__icon" />

                  <span className="sidebar__span">Financeiro</span>
                </li>
              </Link>
            )}

            {auth.perm.includes("r-paSt") && (
              <Link to="/stock" style={{ textDecoration: "none" }}>
                <li className={getItemClass("/stock") + " sidebar__li"}>
                  <PackageCheck className="sidebar__icon" />
                  <span className="sidebar__span">Stock</span>
                </li>
              </Link>
            )}

            {auth.perm.includes("r-paRel") && (
              <Link to="/relatorios" style={{ textDecoration: "none" }}>
                <li className={getItemClass("/relatorios") + " sidebar__li"}>
                  <BarChart3 className="sidebar__icon" />
                  <span className="sidebar__span">Relatórios</span>
                </li>
              </Link>
            )}

            {(auth.role === "Médico/Administrador" ||
              auth.role === "Gerente") && (
              <Link to="/funcionarios" style={{ textDecoration: "none" }}>
                <li className={getItemClass("/funcionarios") + " sidebar__li"}>
                  <UserRoundCog className="sidebar__icon" />

                  <span className="sidebar__span">Funcionários</span>
                </li>
              </Link>
            )}

            {(auth.role === "Médico/Administrador" ||
              auth.role === "Gerente") && (
              <Link to="/users" style={{ textDecoration: "none" }}>
                <li className={getItemClass("/users") + " sidebar__li"}>
                  <Users className="sidebar__icon" />

                  <span className="sidebar__span">Utilizadores</span>
                </li>
              </Link>
            )}

            {(auth.role === "Médico/Administrador" ||
              auth.role === "Gerente") && (
              <Link to="/logs" style={{ textDecoration: "none" }}>
                <li className={getItemClass("/logs") + " sidebar__li"}>
                  <ClipboardList className="sidebar__icon" />

                  <span className="sidebar__span">Registros</span>
                </li>
              </Link>
            )}

            {(auth.role === "Médico/Administrador" ||
              auth.role === "Gerente") &&
              clinica.clinica && (
                <Link
                  to={`/configuracoes/permissoes`}
                  style={{ textDecoration: "none" }}
                >
                  <li
                    className={getItemClass("/configuracoes") + " sidebar__li"}
                  >
                    <Settings className="sidebar__icon" />
                    <span className="sidebar__span">Configurações</span>
                  </li>
                </Link>
              )}

            {auth.role !== "Assistente" && (
              <Link to="/manutencao" style={{ textDecoration: "none" }}>
                <li className={getItemClass("/manutencao") + " sidebar__li"}>
                  <Wrench className="sidebar__icon" />
                  <span className="sidebar__span">Manutenção</span>
                </li>
              </Link>
            )}

            <p className="sidebar__title">AJUDA</p>
            <Link to={`/releasenotes`} style={{ textDecoration: "none" }}>
              <li className={getItemClass("/releasenotes") + " sidebar__li"}>
                <Notebook className="sidebar__icon" />
                <span className="sidebar__span">
                  Atualizações
                  <svg
                    width="10"
                    height="10"
                    style={{ marginLeft: "6px" }}
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="8" cy="8" r="8" fill="#28a745" />
                  </svg>
                </span>
              </li>
            </Link>

            <a
              href="https://docs.healthtechcv.com/docs/introducao"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <li className=" sidebar__li">
                <BookOpenText className="sidebar__icon" />
                <span className="sidebar__span">Documentação</span>
              </li>
            </a>

            <p className="sidebar__title">UTILIZADOR</p>
            <Link
              to={`/users/${auth.userId}/perfil`}
              style={{ textDecoration: "none" }}
            >
              <li className={getItemClass("/perfil") + " sidebar__li"}>
                <CircleUser className="sidebar__icon" />
                <span className="sidebar__span">Perfil</span>
              </li>
            </Link>
            <li className="sidebar__li" onClick={logout}>
              <LogOut className="sidebar__icon" />

              <span className="sidebar__span">Sair</span>
            </li>
          </ul>
        </div>
      )}
    </>
  );
}

export default Sidebar;
